import { canvasToBlob } from './screenshot';

type AnnotationTool = 'pen' | 'highlighter' | 'rectangle' | 'arrow' | 'text';

/**
 * Full-screen annotation overlay.
 * Displays the captured screenshot on a canvas and provides drawing tools
 * (pen, highlighter, rectangle, arrow, text) for the user to annotate.
 */
export class AnnotationOverlay {
  private overlay: HTMLDivElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private baseImage: HTMLCanvasElement;

  private isDrawing = false;
  private currentTool: AnnotationTool = 'pen';
  private currentColor = '#ff0000';
  private drawStartX = 0;
  private drawStartY = 0;

  // Undo stack — stores canvas state after each draw action
  private undoStack: ImageData[] = [];

  // Snapshot of canvas before current shape draw (for rectangle/arrow preview)
  private shapePreviewState: ImageData | null = null;

  private resolvePromise: ((blob: Blob) => void) | null = null;
  private rejectPromise: ((err: Error) => void) | null = null;

  constructor(screenshotCanvas: HTMLCanvasElement) {
    this.baseImage = screenshotCanvas;
  }

  /**
   * Opens the annotation overlay and returns a Promise that resolves
   * with the annotated screenshot as a PNG Blob when the user clicks Done,
   * or rejects if the user cancels.
   */
  open(): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      this.resolvePromise = resolve;
      this.rejectPromise = reject;
      this.render();
    });
  }

  private render(): void {
    // ── Overlay container ──────────────────────────────────────────────
    this.overlay = document.createElement('div');
    this.overlay.id = 'fh-annotation-overlay';
    Object.assign(this.overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483647',
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    });

    // ── Toolbar ────────────────────────────────────────────────────────
    const toolbar = document.createElement('div');
    Object.assign(toolbar.style, {
      display: 'flex',
      gap: '8px',
      padding: '8px 16px',
      background: '#1e1e2e',
      borderRadius: '8px',
      marginBottom: '12px',
      alignItems: 'center',
      flexWrap: 'wrap',
    });

    const tools: Array<{ id: AnnotationTool; label: string; icon: string }> = [
      { id: 'pen', label: 'Pen', icon: '✏️' },
      { id: 'highlighter', label: 'Highlight', icon: '🖍️' },
      { id: 'rectangle', label: 'Rectangle', icon: '▭' },
      { id: 'arrow', label: 'Arrow', icon: '➤' },
      { id: 'text', label: 'Text', icon: 'T' },
    ];

    const toolButtons: HTMLButtonElement[] = [];
    for (const tool of tools) {
      const btn = this.createToolbarButton(`${tool.icon} ${tool.label}`, tool.id === 'pen');
      btn.dataset.tool = tool.id;
      btn.onclick = () => {
        this.currentTool = tool.id;
        toolButtons.forEach((b) => {
          b.style.background = b.dataset.tool === tool.id ? '#585878' : '#2e2e3e';
        });
      };
      toolButtons.push(btn);
      toolbar.appendChild(btn);
    }

    // Color picker
    const colors = ['#ff0000', '#00ff00', '#0088ff', '#ffff00', '#ff00ff', '#ffffff', '#000000'];
    const colorContainer = document.createElement('div');
    Object.assign(colorContainer.style, {
      display: 'flex',
      gap: '4px',
      marginLeft: '12px',
      alignItems: 'center',
    });

    for (const color of colors) {
      const swatch = document.createElement('button');
      Object.assign(swatch.style, {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        border: color === this.currentColor ? '2px solid white' : '2px solid transparent',
        background: color,
        cursor: 'pointer',
        padding: '0',
      });
      swatch.onclick = () => {
        this.currentColor = color;
        colorContainer.querySelectorAll('button').forEach((s) => {
          (s as HTMLButtonElement).style.border =
            (s as HTMLButtonElement).style.background === color
              ? '2px solid white'
              : '2px solid transparent';
        });
      };
      colorContainer.appendChild(swatch);
    }
    toolbar.appendChild(colorContainer);

    // Undo button
    const undoBtn = this.createToolbarButton('↩ Undo', false);
    undoBtn.onclick = () => this.undo();
    undoBtn.style.marginLeft = '12px';
    toolbar.appendChild(undoBtn);

    // Separator + action buttons
    const spacer = document.createElement('div');
    spacer.style.flex = '1';
    toolbar.appendChild(spacer);

    const cancelBtn = this.createToolbarButton('✕ Cancel', false);
    cancelBtn.style.background = '#6e2e2e';
    cancelBtn.onclick = () => this.close(false);
    toolbar.appendChild(cancelBtn);

    const doneBtn = this.createToolbarButton('✓ Done', false);
    doneBtn.style.background = '#2e6e2e';
    doneBtn.onclick = () => this.close(true);
    toolbar.appendChild(doneBtn);

    this.overlay.appendChild(toolbar);

    // ── Drawing canvas ─────────────────────────────────────────────────
    this.canvas = document.createElement('canvas');
    const scale = Math.min(
      (window.innerWidth - 40) / this.baseImage.width,
      (window.innerHeight - 120) / this.baseImage.height,
      1,
    );
    this.canvas.width = this.baseImage.width;
    this.canvas.height = this.baseImage.height;
    Object.assign(this.canvas.style, {
      width: `${this.baseImage.width * scale}px`,
      height: `${this.baseImage.height * scale}px`,
      cursor: 'crosshair',
      borderRadius: '4px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
    });

    this.ctx = this.canvas.getContext('2d')!;
    this.ctx.drawImage(this.baseImage, 0, 0);
    this.saveUndoState();

    // Draw event handlers
    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('mouseup', this.onMouseUp);
    // Touch support
    this.canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
    this.canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.canvas.addEventListener('touchend', this.onTouchEnd);

    this.overlay.appendChild(this.canvas);
    document.body.appendChild(this.overlay);
  }

  // ─── Drawing handlers ────────────────────────────────────────────────

  private getCanvasXY(e: MouseEvent): [number, number] {
    const rect = this.canvas!.getBoundingClientRect();
    const scaleX = this.canvas!.width / rect.width;
    const scaleY = this.canvas!.height / rect.height;
    return [(e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY];
  }

  private onMouseDown = (e: MouseEvent) => {
    this.isDrawing = true;
    const [x, y] = this.getCanvasXY(e);
    this.drawStartX = x;
    this.drawStartY = y;

    if (this.currentTool === 'pen' || this.currentTool === 'highlighter') {
      this.ctx!.beginPath();
      this.ctx!.moveTo(x, y);
    } else if (this.currentTool === 'rectangle' || this.currentTool === 'arrow') {
      this.shapePreviewState = this.ctx!.getImageData(
        0, 0, this.canvas!.width, this.canvas!.height,
      );
    } else if (this.currentTool === 'text') {
      this.isDrawing = false;
      const text = prompt('Enter text:');
      if (text) {
        this.ctx!.font = 'bold 24px sans-serif';
        this.ctx!.fillStyle = this.currentColor;
        this.ctx!.fillText(text, x, y);
        this.saveUndoState();
      }
    }
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.isDrawing || !this.ctx) return;
    const [x, y] = this.getCanvasXY(e);

    if (this.currentTool === 'pen') {
      this.ctx.strokeStyle = this.currentColor;
      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.lineTo(x, y);
      this.ctx.stroke();
    } else if (this.currentTool === 'highlighter') {
      this.ctx.strokeStyle = this.currentColor;
      this.ctx.lineWidth = 20;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.globalAlpha = 0.3;
      this.ctx.lineTo(x, y);
      this.ctx.stroke();
      this.ctx.globalAlpha = 1;
    } else if (this.currentTool === 'rectangle' && this.shapePreviewState) {
      this.ctx.putImageData(this.shapePreviewState, 0, 0);
      this.ctx.strokeStyle = this.currentColor;
      this.ctx.lineWidth = 3;
      this.ctx.strokeRect(
        this.drawStartX, this.drawStartY,
        x - this.drawStartX, y - this.drawStartY,
      );
    } else if (this.currentTool === 'arrow' && this.shapePreviewState) {
      this.ctx.putImageData(this.shapePreviewState, 0, 0);
      this.drawArrow(this.drawStartX, this.drawStartY, x, y);
    }
  };

  private onMouseUp = (_e: MouseEvent) => {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    this.shapePreviewState = null;
    this.saveUndoState();
  };

  // ── Touch handlers (delegate to mouse handlers) ──────────────────────

  private onTouchStart = (e: TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    this.onMouseDown(new MouseEvent('mousedown', {
      clientX: touch.clientX, clientY: touch.clientY,
    }));
  };

  private onTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    this.onMouseMove(new MouseEvent('mousemove', {
      clientX: touch.clientX, clientY: touch.clientY,
    }));
  };

  private onTouchEnd = (_e: TouchEvent) => {
    this.onMouseUp(new MouseEvent('mouseup'));
  };

  // ─── Drawing helpers ─────────────────────────────────────────────────

  private drawArrow(fromX: number, fromY: number, toX: number, toY: number): void {
    const ctx = this.ctx!;
    const headLen = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);

    ctx.strokeStyle = this.currentColor;
    ctx.fillStyle = this.currentColor;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';

    // Line
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.stroke();

    // Arrowhead
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(
      toX - headLen * Math.cos(angle - Math.PI / 6),
      toY - headLen * Math.sin(angle - Math.PI / 6),
    );
    ctx.lineTo(
      toX - headLen * Math.cos(angle + Math.PI / 6),
      toY - headLen * Math.sin(angle + Math.PI / 6),
    );
    ctx.closePath();
    ctx.fill();
  }

  // ─── Undo ────────────────────────────────────────────────────────────

  private saveUndoState(): void {
    if (!this.ctx || !this.canvas) return;
    this.undoStack.push(
      this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height),
    );
    // Keep max 30 states
    if (this.undoStack.length > 30) this.undoStack.shift();
  }

  private undo(): void {
    if (this.undoStack.length <= 1 || !this.ctx) return;
    this.undoStack.pop(); // Remove current state
    const prev = this.undoStack[this.undoStack.length - 1];
    this.ctx.putImageData(prev, 0, 0);
  }

  // ─── Close ───────────────────────────────────────────────────────────

  private async close(save: boolean): Promise<void> {
    if (save && this.canvas) {
      try {
        const blob = await canvasToBlob(this.canvas);
        this.resolvePromise?.(blob);
      } catch (err) {
        this.rejectPromise?.(err instanceof Error ? err : new Error(String(err)));
      }
    } else {
      this.rejectPromise?.(new Error('Annotation cancelled'));
    }
    this.destroy();
  }

  private destroy(): void {
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.onMouseDown);
      this.canvas.removeEventListener('mousemove', this.onMouseMove);
      this.canvas.removeEventListener('mouseup', this.onMouseUp);
      this.canvas.removeEventListener('touchstart', this.onTouchStart);
      this.canvas.removeEventListener('touchmove', this.onTouchMove);
      this.canvas.removeEventListener('touchend', this.onTouchEnd);
    }
    this.overlay?.remove();
    this.overlay = null;
    this.canvas = null;
    this.ctx = null;
    this.undoStack = [];
  }

  // ─── Toolbar helper ──────────────────────────────────────────────────

  private createToolbarButton(label: string, active: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = label;
    Object.assign(btn.style, {
      padding: '6px 12px',
      border: 'none',
      borderRadius: '4px',
      background: active ? '#585878' : '#2e2e3e',
      color: '#e0e0e0',
      cursor: 'pointer',
      fontSize: '13px',
      whiteSpace: 'nowrap',
    });
    btn.addEventListener('mouseenter', () => {
      if (btn.style.background !== '#585878') btn.style.background = '#3e3e4e';
    });
    btn.addEventListener('mouseleave', () => {
      if (btn.style.background !== '#585878') btn.style.background = '#2e2e3e';
    });
    return btn;
  }
}
